import Select, { components } from "react-select";
import { isMobile } from "../common";
import { useMemo, useRef, useLayoutEffect, useState, useCallback } from "react";
import { normalizeDisplayLabel } from "../utility/displayText";

function AutoPositionMenu(props: any) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuShift, setMenuShift] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    const viewportPadding = 8;

    const updateMenuPosition = () => {
      const el = menuRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      let nextX = 0;
      let nextY = 0;

      const rightOverflow = rect.right - (window.innerWidth - viewportPadding);
      if (rightOverflow > 0) {
        nextX -= rightOverflow;
      }

      const leftOverflow = viewportPadding - (rect.left + nextX);
      if (leftOverflow > 0) {
        nextX += leftOverflow;
      }

      const bottomOverflow =
        rect.bottom - (window.innerHeight - viewportPadding);
      if (bottomOverflow > 0) {
        nextY -= bottomOverflow;
      }

      const topOverflow = viewportPadding - (rect.top + nextY);
      if (topOverflow > 0) {
        nextY += topOverflow;
      }

      setMenuShift((previous) => {
        if (previous.x === nextX && previous.y === nextY) {
          return previous;
        }
        return { x: nextX, y: nextY };
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, []);

  const transform =
    menuShift.x !== 0 || menuShift.y !== 0
      ? `translate(${menuShift.x}px, ${menuShift.y}px)`
      : undefined;

  return (
    <div ref={menuRef} style={{ transform }}>
      <components.Menu {...props} />
    </div>
  );
}
type Option = {
  value: string | number;
  label: string;
  isFixed?: boolean;
  isDisabled?: boolean;
};

type SharedSearchProps = {
  options: Option[];
  selectedOptions: Option[];
  placeholder: string;
  minWidth?: number | string;
  maxWidth?: number | string;
  menuMaxHeight?: number;
  isLoading?: boolean;
  menuPortalTarget?: HTMLElement | null;
  isClearable?: boolean;
  isDisabled?: boolean;
  noOptionsText?: string;
  loadingText?: string;
  closeMenuOnSelect?: boolean;
  hideSelectedOptions?: boolean;
  menuPlacement?: "auto" | "top" | "bottom";
  enableSelectAll?: boolean;
  selectAllLabel?: string;
  clearAllLabel?: string;
  maxVisibleTags?: number;
  className?: string;
  isOptionDisabled?: (option: Option) => boolean;
};

type MultiSearchProps = SharedSearchProps & {
  isMany: true;
  onChange: (selected: Option[]) => void;
};

type SingleSearchProps = SharedSearchProps & {
  isMany?: false;
  onChange: (selected: Option | null) => void;
};

type SearchBoxProps = MultiSearchProps | SingleSearchProps;

export const searchStyle = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: isMobile ? 32 : 36,
    borderRadius: 10,
    borderColor: state.isDisabled
      ? "#E5E7EB"
      : state.isFocused
        ? "#F1935C"
        : "#D1D5DB",
    boxShadow: state.isDisabled
      ? "none"
      : state.isFocused
        ? "0 0 0 3px rgba(148,163,184,0.2)"
        : "0 1px 2px rgba(15,23,42,0.04)",
    backgroundColor: state.isDisabled ? "#F3F4F6" : "#FFFFFF",
    "&:hover": {
      borderColor: state.isDisabled
        ? "#E5E7EB"
        : state.isFocused
          ? "#F1935C"
          : "#9CA3AF",
    },
    cursor: state.isDisabled ? "not-allowed" : "default",
    transition: "all 120ms ease",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#6B7280",
    fontSize: isMobile ? 10 : 13,
  }),
  valueContainer: (base: any) => ({
    ...base,
    maxHeight: isMobile ? 32 : 36,
    minHeight: isMobile ? 32 : 36,
    fontSize: isMobile ? 10 : 14,
    overflowX: "hidden",
    overflowY: "auto",
    flexWrap: "wrap" as const,
    scrollbarWidth: "thin" as const,
  }),
  input: (base: any) => ({
    ...base,
    minWidth: "56px",
    flex: "1 1 auto",
  }),
  singleValue: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled ? "#9CA3AF" : "#111827",
  }),
  multiValue: (base: any, state: { data: { isFixed: any } }) => {
    return state.data.isFixed
      ? { ...base, backgroundColor: "#C26B35", flexShrink: 0 }
      : {
          ...base,
          backgroundColor: "#FDE7D9",
          borderRadius: 6,
          flexShrink: 0,
        };
  },
  multiValueLabel: (base: any, state: { data: { isFixed: any } }) => {
    return state.data.isFixed
      ? {
          ...base,
          fontWeight: "bold",
          color: "white",
          paddingRight: 6,
          whiteSpace: "nowrap",
        }
      : { ...base, color: "#A44E1E", fontWeight: 500, whiteSpace: "nowrap" };
  },
  menuList: (base: any) => ({
    ...base,
    maxHeight: 350,
    paddingTop: 4,
    paddingBottom: 4,
    overflowX: "hidden",
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: isMobile ? 10 : 12,
    backgroundColor: state.isDisabled
      ? "#F9FAFB"
      : state.isSelected
        ? "#FEF0E7"
        : state.isFocused
          ? "#FFF8F4"
          : "white",
    color: state.isDisabled ? "#9CA3AF" : "#111827",
    padding: "8px 10px",
    whiteSpace: "normal" as const,
    overflowWrap: "anywhere" as const,
    wordBreak: "break-word" as const,
    lineHeight: 1.25,
    display: "block",
    width: "100%",
    maxWidth: "100%",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
  }),
  multiValueRemove: (
    base: any,
    state: {
      data: {
        value: any;
        isFixed: any;
      };
    },
  ) => {
    return state.data.isFixed ? { ...base, display: "none" } : base;
  },
  menu: (base: any) => ({
    ...base,
    width: "100%",
    minWidth: "100%",
    maxWidth: isMobile ? "92vw" : 420,
    marginTop: 6,
    borderRadius: 10,
    border: "1px solid #E5E7EB",
    boxShadow: "0 10px 24px rgba(15,23,42,0.12)",
    zIndex: 9999,
    overflow: "hidden",
  }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled
      ? "#D1D5DB"
      : state.isFocused
        ? "#F1935C"
        : "#D1D5DB",
    padding: 6,
    transition: "all 120ms ease",
  }),
  clearIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled ? "#D1D5DB" : "#B38164",
    padding: 6,
    ":hover": {
      color: state.isDisabled ? "#D1D5DB" : "#D97C44",
    },
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    marginTop: 6,
    marginBottom: 6,
    backgroundColor: "#F7D7C2",
  }),
  noOptionsMessage: (base: any) => ({
    ...base,
    color: "#6B7280",
    fontSize: isMobile ? 11 : 13,
  }),
  loadingMessage: (base: any) => ({
    ...base,
    color: "#6B7280",
    fontSize: isMobile ? 11 : 13,
  }),
};

export function SearchBox(props: SearchBoxProps) {
  const {
    options,
    selectedOptions,
    onChange,
    placeholder,
    minWidth,
    maxWidth,
    menuMaxHeight,
    isLoading,
    menuPortalTarget,
    isClearable = false,
    isDisabled = false,
    noOptionsText,
    loadingText,
    closeMenuOnSelect,
    hideSelectedOptions = false,
    menuPlacement = "auto",
    enableSelectAll = false,
    selectAllLabel = "Select all",
    clearAllLabel = "Clear",
    className,
    isOptionDisabled,
  } = props;
  const isMany = props.isMany === true;

  const safeOptions = useMemo(() => options ?? [], [options]);
  const safeSelectedOptions = useMemo(
    () => selectedOptions ?? [],
    [selectedOptions],
  );
  const resolvedMinWidth = minWidth ?? (isMobile ? 140 : 180);
  const resolvedMaxWidth = maxWidth ?? (isMobile ? "72vw" : 360);
  const resolvedMenuPortalTarget = useMemo(() => {
    if (menuPortalTarget !== undefined) {
      return menuPortalTarget ?? undefined;
    }
    if (typeof document !== "undefined") {
      return document.body;
    }
    return undefined;
  }, [menuPortalTarget]);

  const value = isMany
    ? safeSelectedOptions.map((option) => ({
        ...option,
        isFixed: option.isFixed ?? false,
      }))
    : safeSelectedOptions[0]
      ? {
          ...safeSelectedOptions[0],
          isFixed: safeSelectedOptions[0].isFixed ?? false,
        }
      : null;

  const resolvedIsOptionDisabled = useCallback(
    (option: Option) => {
      return (
        option.isDisabled === true ||
        (isOptionDisabled != null && isOptionDisabled(option))
      );
    },
    [isOptionDisabled],
  );

  const selectableOptions = useMemo(
    () => safeOptions.filter((option) => !resolvedIsOptionDisabled(option)),
    [safeOptions, resolvedIsOptionDisabled],
  );

  const menuActionsEnabled = isMany && enableSelectAll;

  const MenuListWithActions = useMemo(() => {
    if (!menuActionsEnabled) {
      return undefined;
    }

    return (props: any) => (
      <components.MenuList {...props}>
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            background: "#F8FAFC",
            borderBottom: "1px solid #E5E7EB",
            padding: "6px 8px",
            marginBottom: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: "#6B7280",
              whiteSpace: "nowrap",
            }}
          >
            {safeSelectedOptions.length}/{safeOptions.length} selected
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                (onChange as (selected: Option[]) => void)(selectableOptions);
              }}
              style={{
                border: "none",
                borderRadius: 6,
                background: "#F9DCC9",
                color: "#944A1F",
                fontSize: 11,
                fontWeight: 600,
                padding: "4px 8px",
                cursor: "pointer",
              }}
            >
              {selectAllLabel}
            </button>
            <button
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                (onChange as (selected: Option[]) => void)([]);
              }}
              style={{
                border: "none",
                borderRadius: 6,
                background: "#FFEDE1",
                color: "#B95E2C",
                fontSize: 11,
                fontWeight: 600,
                padding: "4px 8px",
                cursor: "pointer",
              }}
            >
              {clearAllLabel}
            </button>
          </div>
        </div>
        {props.children}
      </components.MenuList>
    );
  }, [
    menuActionsEnabled,
    safeSelectedOptions.length,
    safeOptions.length,
    onChange,
    selectableOptions,
    selectAllLabel,
    clearAllLabel,
  ]);

  const ClearAllIndicator = useMemo(() => {
    if (!isMany) return undefined;

    return (props: any) => {
      return (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            props.clearValue();
          }}
          style={{
            flexShrink: 0,
            border: "none",
            background: "none",
            padding: "0 4px",
            cursor: "pointer",
            color: "#9CA3AF",
            fontSize: 11,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLElement).style.color = "#EF4444")
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLElement).style.color = "#9CA3AF")
          }
        >
          ✕ clear
        </button>
      );
    };
  }, [isMany]);

  const TooltipControl = useMemo(() => {
    if (!isMany) return undefined;
    return (props: any) => {
      const selected = props.getValue() as Option[];
      const title =
        selected.length > 0
          ? selected.map((o) => o.label).join(", ")
          : undefined;
      return (
        <div title={title} style={{ flex: 1, minWidth: 0 }}>
          <components.Control {...props} />
        </div>
      );
    };
  }, [isMany]);

  const CustomOption = useMemo(() => {
    return (props: any) => {
      const label = normalizeDisplayLabel(props.label);
      return (
        <components.Option {...props}>
          <span
            title={label}
            style={{
              display: "block",
              width: "100%",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              lineHeight: 1.25,
            }}
          >
            {label}
          </span>
        </components.Option>
      );
    };
  }, []);

  const CustomSingleValue = useMemo(() => {
    return (props: any) => (
      <components.SingleValue {...props}>
        {normalizeDisplayLabel(props.data.label)}
      </components.SingleValue>
    );
  }, []);

  const CustomMultiValue = useMemo(() => {
    return (props: any) => (
      <components.MultiValue {...props}>
        {normalizeDisplayLabel(props.data.label)}
      </components.MultiValue>
    );
  }, []);

  const customComponents = useMemo(() => {
    const nextComponents: any = {
      Option: CustomOption,
      SingleValue: CustomSingleValue,
      MultiValue: CustomMultiValue,
      Menu: AutoPositionMenu,
    };

    if (MenuListWithActions != null) {
      nextComponents.MenuList = MenuListWithActions;
    }

    if (ClearAllIndicator != null) {
      nextComponents.ClearIndicator = ClearAllIndicator;
    }

    if (TooltipControl != null) {
      nextComponents.Control = TooltipControl;
    }

    return nextComponents;
  }, [
    MenuListWithActions,
    ClearAllIndicator,
    TooltipControl,
    CustomOption,
    CustomSingleValue,
    CustomMultiValue,
  ]);

  const styles = useMemo(() => {
    const base: any = { ...searchStyle };
    base.container = (b: any) => ({
      ...b,
      minWidth: resolvedMinWidth,
      maxWidth: resolvedMaxWidth,
    });
    base.control = (b: any, state: any) => ({
      ...searchStyle.control(b, state),
      minWidth: resolvedMinWidth,
      maxWidth: resolvedMaxWidth,
    });
    base.valueContainer = (b: any) => ({
      ...searchStyle.valueContainer(b),
      maxHeight: isMobile ? 32 : 36,
      minHeight: isMobile ? 32 : 36,
      overflowX: "hidden",
      overflowY: "auto",
      flexWrap: "wrap",
    });
    base.menu = (b: any) => ({
      ...searchStyle.menu(b),
      width: "100%",
      minWidth: "100%",
      maxWidth: isMobile ? "92vw" : 420,
      zIndex: 9999,
    });

    if (menuMaxHeight != null) {
      base.menuList = (b: any) => ({
        ...b,
        maxHeight: menuMaxHeight,
        paddingTop: 0,
        paddingBottom: 0,
      });
    }
    base.menuPortal = (b: any) => ({ ...b, zIndex: 999999 });

    return base;
  }, [menuMaxHeight, resolvedMaxWidth, resolvedMinWidth]);

  const handleChange = (nextValue: any) => {
    if (isMany) {
      (onChange as (selected: Option[]) => void)((nextValue ?? []) as Option[]);
      return;
    }
    (onChange as (selected: Option | null) => void)(
      (nextValue ?? null) as Option | null,
    );
  };

  const filterOption = (candidate: any, inputValue: string) => {
    const query = inputValue.trim().toLowerCase();
    if (!query) {
      return true;
    }
    const cleanLabel = normalizeDisplayLabel(candidate.label);
    return (
      cleanLabel.toLowerCase().includes(query) ||
      candidate.label.toLowerCase().includes(query) ||
      String(candidate.value).toLowerCase().includes(query)
    );
  };

  const resolvedCloseMenuOnSelect =
    closeMenuOnSelect ?? (isMany ? false : true);

  return (
    <Select
      className={className ?? "text-sm text-gray-800"}
      placeholder={placeholder}
      value={value as any}
      isMulti={isMany}
      onChange={handleChange as any}
      styles={styles}
      options={safeOptions as any}
      isLoading={isLoading}
      loadingMessage={() => loadingText ?? "Loading options..."}
      noOptionsMessage={() => noOptionsText ?? "No options found"}
      filterOption={filterOption}
      isSearchable
      isClearable={
        isMany ? safeSelectedOptions.some((o) => !o.isFixed) : isClearable
      }
      isDisabled={isDisabled}
      hideSelectedOptions={hideSelectedOptions}
      closeMenuOnSelect={resolvedCloseMenuOnSelect}
      menuPlacement={menuPlacement}
      menuPortalTarget={resolvedMenuPortalTarget}
      menuPosition={resolvedMenuPortalTarget != null ? "fixed" : "absolute"}
      isOptionDisabled={resolvedIsOptionDisabled as any}
      components={customComponents}
    />
  );
}

type Props = {
  start: { year: number; month: number };
  end: { year: number; month: number };
  selectedOptions: Option[];
  onChange: (selected: Option[]) => void;
  isMany?: boolean;
  placeholder?: string;
  customRanges?: Option[]; // Optional custom date ranges
};

export const generateMonthRanges = (
  start: { year: number; month: number },
  end: { year: number; month: number },
) => {
  const result: Option[] = [];
  let currentYear = start.year;
  let currentMonth = start.month;

  while (
    currentYear < end.year ||
    (currentYear === end.year && currentMonth <= end.month)
  ) {
    const startDate = new Date(currentYear, currentMonth - 1, 1);
    const endDate = new Date(currentYear, currentMonth, 0);

    const formattedStart = startDate.toISOString().split("T")[0];
    const formattedEnd = endDate.toISOString().split("T")[0];
    const monthLabel = startDate
      .toLocaleString("default", { month: "short" })
      .toUpperCase();
    const yearLabel = String(currentYear).slice(-2);

    result.push({
      value: `${formattedStart},${formattedEnd}`,
      label: `${monthLabel}-${yearLabel}`,
    });

    if (currentMonth === 12) {
      currentMonth = 1;
      currentYear++;
    } else {
      currentMonth++;
    }
  }
  return result;
};

const SelectOption: React.FC<Props> = ({
  start,
  end,
  selectedOptions,
  onChange,
  isMany = false,
  placeholder = "Select Date Range",
  customRanges = [],
}) => {
  const options = generateMonthRanges(start, end).concat(customRanges);

  if (isMany) {
    return (
      <SearchBox
        options={options}
        selectedOptions={selectedOptions}
        onChange={onChange}
        placeholder={placeholder}
        isMany={true}
      />
    );
  }

  return (
    <SearchBox
      options={options}
      selectedOptions={selectedOptions}
      onChange={(selected) => {
        if (selected == null) {
          onChange([]);
          return;
        }
        onChange([selected]);
      }}
      placeholder={placeholder}
      isMany={false}
    />
  );
};

export default SelectOption;
